import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import GoToDashboardButton from './GoToDashboardButton';
import styles from './ProductionRootCause.module.css';
import SourceFileSummary from './SourceFileSummary';
import {
  artavaRechartsAxis,
  artavaRechartsGrid,
  artavaRechartsTooltip,
} from './chartPalette';
import { fetchFirstFailAnalysis } from './productionRootCause/productionRootCauseApi';

const defaultFilters = {
  startDate: '',
  endDate: '',
  ateSwVersion: '',
  stationName: '',
  selectedFamilies: [],
};

const FirstFailFingerprint = () => {
  const [filters, setFilters] = useState(defaultFilters);
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const chartRows = useMemo(() => (
    data.rows.slice(0, 12).map((row) => ({
      step: row.stepNumber,
      name: row.stepName,
      firstFails: row.firstFailSessions,
      repeatFails: row.failStepCount,
      score: row.score,
    }))
  ), [data.rows]);

  const loadData = async (nextFilters = filters) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetchFirstFailAnalysis(nextFilters);
      setData(response || { summary: null, rows: [] });
    } catch (requestError) {
      setError(requestError.message || 'Failed to load first-fail analysis.');
      setData({ summary: null, rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    loadData(defaultFilters);
  };

  const summary = data.summary || {};

  return (
    <div className={styles.pageContainer}>
      <GoToDashboardButton />

      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Root problem idea 1</p>
          <h1 className={styles.title}>First-Fail Fingerprint</h1>
          <p className={styles.description}>
            Start with the first failing step in each failed UUT. This separates primary triggers from later cascade failures and makes repeat serial patterns easier to see.
          </p>
        </div>
      </header>

      <section className={styles.filterPanel}>
        <div className={styles.filterHeader}>
          <div>
            <h3>Filters</h3>
            <p>Use the same production slice across charts when you are comparing shifts, versions, or stations.</p>
          </div>
        </div>
        <div className={styles.filterGrid}>
          <div className={styles.filterItem}>
            <label>Start date</label>
            <input type="date" value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>End date</label>
            <input type="date" value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
          </div>
          <div className={styles.filterItem}>
            <label>ATE SW version</label>
            <input value={filters.ateSwVersion} onChange={(event) => updateFilter('ateSwVersion', event.target.value)} placeholder="All versions" />
          </div>
          <div className={styles.filterItem}>
            <label>Station name</label>
            <input value={filters.stationName} onChange={(event) => updateFilter('stationName', event.target.value)} placeholder="All stations" />
          </div>
          <div className={styles.filterActions}>
            <button className={styles.primaryButton} onClick={() => loadData()} disabled={loading}>Run analysis</button>
            <button className={styles.secondaryButton} onClick={resetFilters}>Reset</button>
          </div>
        </div>
        <SourceFileSummary
          compact
          startDate={filters.startDate}
          endDate={filters.endDate}
          ateSwVersion={filters.ateSwVersion}
          selectedFamilies={filters.selectedFamilies}
          onFamiliesChange={(values) => updateFilter('selectedFamilies', values)}
          title="Source files used for dashboard charts"
        />
      </section>

      {error && <div className={styles.error}>{error}</div>}

      <section className={styles.metricGrid}>
        <div className={styles.metricCard}><span>Sessions</span><strong>{summary.sessions || 0}</strong></div>
        <div className={styles.metricCard}><span>Failed sessions</span><strong>{summary.failedSessions || 0}</strong></div>
        <div className={styles.metricCard}><span>First-fail patterns</span><strong>{summary.firstFailPatterns || 0}</strong></div>
        <div className={styles.metricCard}><span>Top pattern share</span><strong>{summary.topFirstFailShare || 0}%</strong></div>
      </section>

      {loading ? (
        <div className={styles.loading}>Analyzing first failing steps...</div>
      ) : data.rows.length === 0 ? (
        <div className={styles.emptyState}>No first-fail patterns matched this slice.</div>
      ) : (
        <>
          <section className={styles.chartPanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Primary failure triggers</h3>
                <p>Bars rank the steps that appear first in failed sessions; the darker score includes repeat failures and affected serials.</p>
              </div>
            </div>
            <div className={styles.chartBox}>
              <ResponsiveContainer>
                <BarChart data={chartRows} margin={{ top: 10, right: 20, bottom: 40, left: 0 }}>
                  <CartesianGrid {...artavaRechartsGrid} vertical={false} />
                  <XAxis dataKey="step" angle={-35} textAnchor="end" height={70} {...artavaRechartsAxis} />
                  <YAxis {...artavaRechartsAxis} />
                  <Tooltip {...artavaRechartsTooltip} />
                  <Bar dataKey="firstFails" name="First fails" fill="#24745f" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="score" name="Root score" fill="#4f7f99" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className={styles.insightGrid}>
            {data.rows.slice(0, 3).map((row) => (
              <article className={styles.insightCard} key={row.stepNumber}>
                <p className={styles.eyebrow}>Candidate root cause</p>
                <h3>{row.stepNumber} - {row.stepName}</h3>
                <p>{row.firstFailSessions} sessions failed here first, covering {row.affectedSerials} serials and {row.firstFailShare}% of all first-fail patterns.</p>
                <div className={styles.chips}>
                  {row.serials.map((serial) => <span key={serial}>SN {serial}</span>)}
                  {row.ateSwVersions.map((version) => <span key={version}>{version}</span>)}
                </div>
              </article>
            ))}
          </section>

          <section className={styles.tablePanel}>
            <div className={styles.panelHeader}>
              <div>
                <h3>Evidence table</h3>
                <p>Use the first-fail share to decide what to investigate before chasing downstream failures.</p>
              </div>
            </div>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Step</th>
                  <th>First fails</th>
                  <th>Share</th>
                  <th>All fail rows</th>
                  <th>Serials</th>
                  <th>Avg duration</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.stepNumber}>
                    <td>{row.rank}</td>
                    <td className={styles.stepName}>{row.stepNumber}<br /><span className={styles.muted}>{row.stepName}</span></td>
                    <td>{row.firstFailSessions}</td>
                    <td>{row.firstFailShare}%</td>
                    <td>{row.failStepCount}</td>
                    <td>{row.affectedSerials}</td>
                    <td>{row.avgDurationMinutes} min</td>
                    <td>{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
};

export default FirstFailFingerprint;
